/**
 * The keyboard scheme.
 *
 * Two rules from §9.2 shape everything here, and both are the kind that is
 * trivially violated by a `keydown` listener written per surface:
 *
 * 1. **Nothing fires while text entry has focus.** The native app declares its
 *    shortcuts twice — on the standard deck and again on the accessibility deck
 *    — which is precisely how a layout change silently drops one. Here every
 *    binding goes through one registry, so the guard cannot be forgotten by a
 *    surface that registers late.
 * 2. **No reserved browser shortcut is captured.** ⌘1–4, ⌘N, ⌘S and ⌥Space all
 *    collide with browser or OS chrome, so the native scheme is remapped rather
 *    than transcribed. `isReservedBrowserShortcut` refuses the collisions at
 *    registration time — a loud failure in development beats a user discovering
 *    that the app ate their "new tab".
 */

import type { ReactNode } from "react";
import { createContext, useCallback, useContext, useEffect, useMemo, useRef } from "react";
import type { Cancel } from "@ff/domain/ports";

// ---------------------------------------------------------------------------
// Bindings
// ---------------------------------------------------------------------------

export interface ShortcutBinding {
  /**
   * `KeyboardEvent.key`. Letters are matched case-insensitively so a binding
   * still fires with Caps Lock on; everything else is matched exactly.
   */
  readonly key: string;
  readonly meta?: boolean;
  readonly ctrl?: boolean;
  readonly alt?: boolean;
  readonly shift?: boolean;
}

function modifiers(binding: ShortcutBinding): {
  meta: boolean;
  ctrl: boolean;
  alt: boolean;
  shift: boolean;
} {
  return {
    meta: binding.meta === true,
    ctrl: binding.ctrl === true,
    alt: binding.alt === true,
    shift: binding.shift === true,
  };
}

export function matchesBinding(binding: ShortcutBinding, event: KeyboardEvent): boolean {
  const wanted = modifiers(binding);
  if (event.metaKey !== wanted.meta) return false;
  if (event.ctrlKey !== wanted.ctrl) return false;
  if (event.altKey !== wanted.alt) return false;
  if (event.shiftKey !== wanted.shift) return false;
  return event.key.toLowerCase() === binding.key.toLowerCase();
}

const FUNCTION_KEY = /^F([1-9]|1[0-2])$/;

/**
 * Whether a browser or the host OS already owns this combination.
 *
 * The test is deliberately broad rather than an enumeration of one browser's
 * menu: the same combination means different things on Chrome, Safari and
 * Firefox, and on Windows Firefox even ⌥1–9 switches tabs. Refusing whole
 * *shapes* is the only version of this rule that stays true across all of them.
 */
export function isReservedBrowserShortcut(binding: ShortcutBinding): boolean {
  const { meta, ctrl, alt } = modifiers(binding);

  // Tab and its shift form are focus movement, never an application command.
  if (binding.key === "Tab") return true;
  // Bare function keys are browser chrome (F5 reload, F11 fullscreen, F12 tools).
  if (FUNCTION_KEY.test(binding.key)) return true;

  // ⌃⌘ on macOS is system chrome (⌃⌘Space, ⌃⌘F).
  if (meta && ctrl) return true;

  const primary = meta || ctrl;
  // A primary modifier without Alt is the whole browser menu: ⌘1–9/⌃1–9 tabs,
  // ⌘N/⌃N window, ⌘S save, ⌘⇧T reopen, and the rest.
  if (primary && !alt) return true;

  // Alt+digit switches tabs in Firefox on Windows and Linux.
  if (alt && !primary && /^[0-9]$/.test(binding.key)) return true;
  // Alt+Left/Right is back/forward.
  if (alt && !primary && (binding.key === "ArrowLeft" || binding.key === "ArrowRight")) {
    return true;
  }

  return false;
}

// ---------------------------------------------------------------------------
// The text-entry guard
// ---------------------------------------------------------------------------

/**
 * `<input type="checkbox">` and friends are not text entry, but they are also
 * not worth the risk: a shortcut that steals Space from a checkbox is the same
 * defect in a different costume. Any `input` counts.
 */
export function isTextEntryTarget(target: EventTarget | null): boolean {
  if (target === null || !(target instanceof Element)) return false;
  const tag = target.tagName.toLowerCase();
  if (tag === "input" || tag === "textarea" || tag === "select") return true;
  if (target instanceof HTMLElement && target.isContentEditable) return true;
  // A composed rich-text surface can put focus on a descendant of the editable
  // host, so the ancestor chain is what decides, not the event target alone.
  return target.closest('[contenteditable="true"], [contenteditable=""]') !== null;
}

// ---------------------------------------------------------------------------
// Registry
// ---------------------------------------------------------------------------

interface ShortcutEntry {
  readonly binding: ShortcutBinding;
  readonly handler: () => void;
  readonly enabled: () => boolean;
}

class ShortcutRegistry {
  readonly #entries: ShortcutEntry[] = [];

  register(entry: ShortcutEntry): Cancel {
    this.#entries.push(entry);
    return () => {
      const index = this.#entries.indexOf(entry);
      if (index >= 0) this.#entries.splice(index, 1);
    };
  }

  /**
   * The most recently registered enabled match wins and nothing else runs. A
   * presented sheet registers after the surface underneath it, so "last wins"
   * is what keeps a shortcut from firing on a screen the user cannot see.
   */
  handle(event: KeyboardEvent): void {
    if (event.defaultPrevented) return;
    if (isTextEntryTarget(event.target)) return;
    for (let index = this.#entries.length - 1; index >= 0; index -= 1) {
      const entry = this.#entries[index];
      if (entry === undefined) continue;
      if (!entry.enabled()) continue;
      if (!matchesBinding(entry.binding, event)) continue;
      event.preventDefault();
      entry.handler();
      return;
    }
  }
}

const ShortcutContext = createContext<ShortcutRegistry | null>(null);

export interface ShortcutProviderProps {
  /** Defaults to the document. A test may pass its own container. */
  readonly target?: EventTarget | null;
  readonly children?: ReactNode;
}

export function ShortcutProvider({ target, children }: ShortcutProviderProps): ReactNode {
  const registry = useMemo(() => new ShortcutRegistry(), []);

  useEffect(() => {
    const node = target ?? (typeof document === "undefined" ? null : document);
    if (node === null) return;
    const listener = (event: Event): void => {
      registry.handle(event as KeyboardEvent);
    };
    node.addEventListener("keydown", listener);
    return () => {
      node.removeEventListener("keydown", listener);
    };
  }, [registry, target]);

  return <ShortcutContext.Provider value={registry}>{children}</ShortcutContext.Provider>;
}

function useShortcutRegistry(): ShortcutRegistry {
  const registry = useContext(ShortcutContext);
  if (registry === null) {
    throw new Error(
      "useShortcut() requires an enclosing <ShortcutProvider>, placed by <AppStateProvider>.",
    );
  }
  return registry;
}

export interface ShortcutOptions {
  /**
   * A disabled binding stays registered but never fires, so the ordering a
   * surface established at mount is not reshuffled every time a control's
   * availability flickers.
   */
  readonly enabled?: boolean;
}

export function useShortcut(
  binding: ShortcutBinding,
  handler: () => void,
  options?: ShortcutOptions,
): void {
  const registry = useShortcutRegistry();
  const handlerRef = useRef(handler);
  handlerRef.current = handler;
  const enabled = options?.enabled ?? true;
  const enabledRef = useRef(enabled);
  enabledRef.current = enabled;

  const { key, meta, ctrl, alt, shift } = binding;
  const stableBinding = useMemo<ShortcutBinding>(
    () => ({ key, meta, ctrl, alt, shift }),
    [key, meta, ctrl, alt, shift],
  );

  useEffect(() => {
    if (isReservedBrowserShortcut(stableBinding)) {
      throw new Error(
        `Refusing to bind ${describeShortcut(stableBinding, false)}: it is reserved by the ` +
          "browser or the operating system (spec §9.2). Choose an unreserved combination.",
      );
    }
    return registry.register({
      binding: stableBinding,
      // Read through refs so a re-rendered handler does not re-register and
      // jump this binding to the front of the "last wins" order.
      handler: () => {
        handlerRef.current();
      },
      enabled: () => enabledRef.current,
    });
  }, [registry, stableBinding]);
}

// ---------------------------------------------------------------------------
// Rendering a binding
// ---------------------------------------------------------------------------

const KEY_LABEL: Readonly<Record<string, string>> = {
  ArrowLeft: "←",
  ArrowRight: "→",
  ArrowUp: "↑",
  ArrowDown: "↓",
  Enter: "Return",
  Escape: "Esc",
  " ": "Space",
};

/**
 * The hint the sidebar renders. It is generated from the binding rather than
 * written as a literal because §9.2 names the failure directly: the native
 * sidebar renders the string "⌘1–4 switches sections", and a remapped scheme
 * with a stale hint teaches the user a shortcut that does not exist.
 */
export function describeShortcut(binding: ShortcutBinding, applePlatform: boolean): string {
  const { meta, ctrl, alt, shift } = modifiers(binding);
  const label = KEY_LABEL[binding.key] ?? binding.key.toUpperCase();
  if (applePlatform) {
    return `${ctrl ? "⌃" : ""}${alt ? "⌥" : ""}${shift ? "⇧" : ""}${meta ? "⌘" : ""}${label}`;
  }
  const parts: string[] = [];
  if (ctrl) parts.push("Ctrl");
  if (alt) parts.push("Alt");
  if (shift) parts.push("Shift");
  if (meta) parts.push("Win");
  parts.push(label);
  return parts.join("+");
}

/** Pure so the platform read stays in one place and the rule stays testable. */
export function isApplePlatformUserAgent(userAgent: string): boolean {
  return /Mac|iPhone|iPad|iPod/i.test(userAgent);
}

export function useApplePlatform(): boolean {
  return useMemo(() => {
    if (typeof navigator === "undefined") return false;
    return isApplePlatformUserAgent(navigator.userAgent);
  }, []);
}

/**
 * Renders a binding the way the current platform writes it, so one hint string
 * serves both without a second literal to keep in step.
 */
export function useShortcutLabel(): (binding: ShortcutBinding) => string {
  const applePlatform = useApplePlatform();
  return useCallback(
    (binding: ShortcutBinding) => describeShortcut(binding, applePlatform),
    [applePlatform],
  );
}
